import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Notifications } from '../../store/slices';
import { downloadBestQuality, IBestRendition, IDownloadProgress } from '../../lib/storyViewer';

export interface IVideoDownloadState {
    status: 'idle' | 'probing' | 'downloading' | 'done' | 'error';
    progress: IDownloadProgress | null;
    result: IBestRendition | null;
    /** The videoLink this state belongs to, so a modal that switches stories can tell whose progress it is. */
    videoLink: string | null;
}

const IDLE: IVideoDownloadState = { status: 'idle', progress: null, result: null, videoLink: null };

/** Drives one "download best quality" action with progress and cancellation. */
export const useVideoDownload = () => {
    const dispatch = useDispatch();
    const [state, setState] = useState<IVideoDownloadState>(IDLE);
    const controllerRef = useRef<AbortController | null>(null);

    useEffect(() => () => controllerRef.current?.abort(), []);

    const start = useCallback(async (videoLink: string, fileBase: string) => {
        controllerRef.current?.abort();
        const controller = new AbortController();
        controllerRef.current = controller;
        setState({ status: 'probing', progress: null, result: null, videoLink });
        try {
            const result = await downloadBestQuality({
                videoLink,
                fileBase,
                signal: controller.signal,
                onProgress: (progress) => {
                    if (controller.signal.aborted) return;
                    setState({ status: 'downloading', progress, result: null, videoLink });
                },
            });
            if (controller.signal.aborted) return;
            setState({ status: 'done', progress: null, result, videoLink });
            dispatch(Notifications.setNotifications({ text: `Downloaded ${result.resolution}p video`, type: 'success' }));
        } catch (error: any) {
            if (controller.signal.aborted || error?.name === 'AbortError') {
                setState(IDLE);
                return;
            }
            setState({ status: 'error', progress: null, result: null, videoLink });
            dispatch(Notifications.setNotifications({ text: error?.message || 'Download failed', type: 'error' }));
        } finally {
            if (controllerRef.current === controller) controllerRef.current = null;
        }
    }, [dispatch]);

    const cancel = useCallback(() => {
        controllerRef.current?.abort();
        controllerRef.current = null;
        setState(IDLE);
    }, []);

    return { state, start, cancel, busy: state.status === 'probing' || state.status === 'downloading' };
};
